<template>
  <div class="transaction-item py-2" @click="onClick">
    <div class="icon-container mb-auto mt-0">
      <img
        :src="`${useAsset(icon)}`"
        alt="transaction icon"
        class="my-auto"
        width="24"
        height="24"
      />
    </div>
    <div class="w-100 ms-0">
      <p class="w-100 row justify-content-between m-0 p-0">
        <span class="fw-bold text-dark text-start col col-7">{{
          transactionStyle.header
        }}</span>
        <span class="fw-bold text-dark text-end me-0 col p-0">{{
          displayTransactionAmount(amount)
        }}</span>
      </p>
      <p class="w-100 row justify-content-between m-0 p-0">
        <span class="text-start me-auto col">{{
          transactionStyle.method
        }}</span>
        <span class="text-end ms-auto me-0 col p-0">
          {{ displayTransactionDate(date) }}
        </span>
      </p>
    </div>
    <div>
      <i class="fa fa-chevron-right mt-3 ms-2"></i>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { shortDateFormatHelper } from "@/apps/credit-line/helpers/dateFormat";
import {
  TransactionStatus,
  TransactionType,
} from "@/apps/credit-line/models/CreditLineDetails";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";

export default defineComponent({
  name: "TransactionItem",
  computed: {
    TransactionType() {
      return TransactionType;
    },
  },
  props: {
    icon: {
      type: String,
      default: "",
    },
    amount: {
      type: String,
    },
    status: String as PropType<TransactionStatus>,
    type: {
      type: String as PropType<TransactionType>,
    },
    date: {
      type: Number,
    },
    method: {
      type: String,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    refundDebit: String,
  },
  emits: ["onclick"],
  methods: {
    onClick() {
      this.$emit("onclick");
    },
  },
  setup(props) {
    const { t, te, locale } = useI18n();
    type transactionStyleType = {
      header: string;
      method: string;
    };
    const displayTransactionAmount = (val: string): string => {
      const newVal = currencyFormat(Number(val));
      return Number(val) < 0 ? `-${newVal.replace("-", "")}` : `+${newVal}`;
    };

    const displayTransactionDate = (val: number): string => {
      const date = new Date(val * 1000);
      return props.showDate
        ? shortDateFormatHelper(date.toLocaleString("en-US"), locale.value)
        : date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
    };

    const transactionStyle = computed((): transactionStyleType => {
      let header = "";
      let method = "";
      switch (props.type) {
        case TransactionType.PHONE_PURCHASE:
          header = t("CL_TRANSACTIONS.PHONE_PURCHASE_ITEM_HEADER");
          method = t("CL_TRANSACTIONS.PHONE_PURCHASE");
          break;
        case TransactionType.CREDITCARD:
          header =
            props.method === TransactionStatus.REJECTED
              ? t("CL_TRANSACTIONS.CARD_DECLINED")
              : te(`CL_TRANSACTIONS.${props.method}`)
                ? t(`CL_TRANSACTIONS.${props.method}`)
                : (props.method as string);
          method =
            props.method === TransactionStatus.REJECTED
              ? t("CL_TRANSACTIONS.CARD_DECLINED")
              : t("CREDITCARD");
          break;
        case TransactionType.WITHDRAWAL:
          header = t("WITHDRAWAL");
          method = t("CL_TRANSACTIONS.CASH_WITHDRAWALS_ITEM");
          break;
        case TransactionType.CASH_TRANSFER:
          header = t("WITHDRAWAL");
          method = t("CL_TRANSACTIONS.ONLINE_TRANSFER");
          break;
        case TransactionType.REFUND:
          header = props.refundDebit ?? t("CL_TRANSACTIONS.REFUND");
          method = t("CL_TRANSACTIONS.CARD_REFUND");
          break;
        case TransactionType.REVERSAL_PURCHASE:
          header = props.refundDebit ?? t("CL_TRANSACTIONS.REVERSAL_PURCHASE");
          method = t("CL_TRANSACTIONS.CARD_REFUND");
          break;
        case TransactionType.PURCHASE_ADJUSTMENT:
          header = t("CL_TRANSACTIONS.PURCHASE_ADJUSTMENT");
          method = t("CREDITCARD");
          break;
        case TransactionType.PAYMENT:
          header = t("CL_TRANSACTIONS.PAYMENT_ITEM_HEADER");
          method =
            t("CL_TRANSACTIONS.PAYMENT_BY") +
            " " +
            (te(`CL_TRANSACTIONS.${props.method}`)
              ? t(`CL_TRANSACTIONS.${props.method}`)
              : (props.method as string));
          break;
        case TransactionType.CASH_BACK:
          header = t("CL_TRANSACTIONS.REWARD_EARNED");
          method = t("CL_TRANSACTIONS.CONGRATS");
          break;
        case TransactionType.PAYMENT_REMAINDER:
          header = t("CL_TRANSACTIONS.PAYMENT_ITEM_HEADER");
          method =
            t("CL_TRANSACTIONS.PAYMENT_BY") +
            " " +
            t("CL_TRANSACTIONS.CREDIT_ADJUSTMENT_DESCRIPTION").toLowerCase();
          break;
        case TransactionType.BUNDLE:
          header = t("CL_TRANSACTIONS.BUNDLE");
          method =
            t("CL_TRANSACTIONS.PAYMENT_BY") +
            " " +
            t("CL_TRANSACTIONS.CREDIT").toLowerCase();
          break;
        default:
          break;
      }

      return { header, method };
    });

    return {
      t,
      transactionStyle,
      useAsset,
      displayTransactionDate,
      displayTransactionAmount,
    };
  },
});
</script>

<style lang="scss" scoped>
.transaction-item {
  cursor: pointer;
  display: flex;
  flex-direction: row;
}
</style>
